import he from 'he';
import fp from 'lodash/fp';
import React from 'react';
import { Button, Col, Modal, Row } from 'react-bootstrap';
import { connect } from 'react-redux';
import Spinner from 'react-spinkit';
import { compose, lifecycle, withHandlers, withProps, withStateHandlers } from 'recompose';
import { bindActionCreators } from 'redux';
import { handleInvalidForm, isExternalUser, validateEmail } from '../../../../../services';
import { businessActions, businessSelector } from '../../../../../state/modules/business';
import { contentConfigSelector } from '../../../../../state/modules/configuration/selectors';
import { currentUserSelector, userAfslSelector } from '../../../../../state/modules/user';
import { FileUploadFormField, FormSubmissionMessage, Icon, ImageCropField, InputFormField } from '../../../../components';
import { AfslSetting } from '../../professional-profile/partials';

const BusinessDetails = ({
  user,
  afsLicence,
  showSubmissionMessage,
  submissionMessage,
  error,
  stopFocusingMessage,
  hideSubmissionMessage,
  focusMessage,
  isSubmittingForm,
  isUpdatingDetails,
  validation,
  businessDetails,
  updateBusinessDetails,
  componentRef,
  croppedImageUrl,
  onBusinessDetailFormChange,
  onImageCropped,
  onImageDrop,
  businessImage,
  businessImageUrl,
  maxImageFileSizeBytes,
  removeImage,
  isExternalUser,
  showingCroppingModal,
  hideCroppingModal
}) => [
    <div key='business-details-container' className='business-details'>
      <Row>
        <Col xs={12} sm={7} className='col-left'>
          <form
            ref={(form) => { if (componentRef) componentRef.formBusinessDetail = form; }}
            noValidate
            onSubmit={updateBusinessDetails}>
            
            <div className='well company-details-well'>
              <h2>
                Update your company details
              </h2>

              <FormSubmissionMessage {...{
                showSubmissionMessage,
                submissionMessage,
                error, 
                focusMessage,
                stopFocusingMessage,
                hideSubmissionMessage
              }} />

              <fieldset>
                <InputFormField
                  id='business-name'
                  required
                  validation={validation.name ? null : 'Business name is required.'}
                  value={businessDetails.name}
                  onChange={(e) => onBusinessDetailFormChange(e, 'name')} />
                <InputFormField
                  id='business-email'
                  required
                  disabled={isExternalUser}
                  validation={validation.email ? null : 'Please provide valid email address.'}
                  value={businessDetails.email}
                  onChange={(e) => onBusinessDetailFormChange(e, 'email')} />
                
                <div className='logo-upload-container'>
                  {
                    businessImageUrl && !businessImage ?
                      [<div key='img-exist' className='business-logo existing'><img alt="business logo" src={he.decode(businessImageUrl)} /></div>,
                      <button key='btn-exist' aria-label='Remove Image' className='btn remove-business-logo' onClick={removeImage}>
                        <Icon icon='close' />
                      </button>]
                      : null
                  }
                  {
                    croppedImageUrl ? [
                      <div key='img-new' className='business-logo new'><img alt="business logo" src={croppedImageUrl} /></div>,
                      <button key='remove-button' aria-label='Remove Image' className='btn remove-business-logo under-crop-field' onClick={removeImage}>
                        <Icon icon='close' />
                      </button>
                    ] : null
                  }
                  {
                    !businessImageUrl && !businessImage ? <FileUploadFormField
                      styleName='file-6'
                      multiple={false}
                      accept='image/png,image/jpeg'
                      onFileDrop={onImageDrop}
                      file={businessImage}
                      maxSize={maxImageFileSizeBytes}
                      dropLabel='Upload Logo (JPG, PNG)' /> : null
                  }
                </div>
                <Button type='submit' className='large' disabled={isSubmittingForm}>
                  {
                    isUpdatingDetails ?
                      <Spinner name='circle' className='spinner' fadeIn='none' /> :
                      'UPDATE BUSINESS DETAILS'
                  }
                </Button>
              </fieldset>
            </div>
          </form>
        </Col>
        
        <Col xs={12} sm={5} className='col-right'>
          <div className='well afsl-well'>
            <AfslSetting afsLicence={afsLicence} user={user} />
          </div>
        </Col>
      </Row>
      <Modal show={showingCroppingModal} onHide={hideCroppingModal} dialogClassName='image-cropping-modal'>
        <Modal.Body>
          <ImageCropField
            className='business-logo new'
            imageSrc={businessImage ? businessImage.preview : null}
            onComplete={onImageCropped} />
          <button aria-label='Remove Image' className='btn remove-business-logo under-crop-field' onClick={removeImage}>
            <Icon icon='close' />
          </button>
        </Modal.Body>
      </Modal>
    </div>
  ];

const validateBusinessDetailForm = ({
  businessDetails: {
    email,
    name
  },
  setValidation
}) => fp.flow(
  fp.flow(
    fp.over([
      setValidation,
      fp.identity
    ]),
    fp.last
  ),
  fp.all(fp.identity)
)({
  name: fp.flow(fp.trim, fp.negate(fp.isEmpty))(name),
  email: validateEmail(email)
});

const mapStateToProps = (state) => ({
  user: currentUserSelector(state),
  business: businessSelector(state),
  afsLicence: userAfslSelector(state),
  error: state.business.error,
  isUpdating: state.business.isUpdating,
  config: contentConfigSelector(state),
});

const mapDispatchToProps = (dispatch) => ({
  updateBusiness: bindActionCreators(businessActions.business.updateBusiness, dispatch)
});

export default compose(
  connect(mapStateToProps, mapDispatchToProps),
  withProps(({ config, user }) => ({
    maxImageFileSizeBytes: config.maxImageFileSizeBytes,
    isExternalUser: isExternalUser(user)
  })),
  withStateHandlers(({ business }) => ({
    businessDetails: {
      name: '',
      email: ''
    },
    validation: {
      email: true,
      name: true
    },
    isUpdatingDetails: false,
    showSubmissionMessage: false,
    focusMessage: false,
    loadedBusinessDetails: false,
    submissionMessage: ''
  }),
    {
      onBusinessDetailFormChange: ({
        businessDetails
      }) => (event, businessProperty) => ({
        businessDetails: {
          ...businessDetails,
          [businessProperty]: event.target.value
        }
      }),
      setValidation: () => (validation) => ({
        validation
      }),
      startUpdatingBusinessDetails: () => (message) => ({
        showSubmissionMessage: false,
        isUpdatingDetails: true,
        submissionMessage: message
      }),
      updateFinished: () => () => ({
        isUpdatingDetails: false,
        focusMessage: true,
        showSubmissionMessage: true
      }),
      setBusinessDetails: ({
        businessDetails
      }) => (business) => ({
        businessDetails: {
          ...businessDetails,
          name: he.decode(business.name),
          email: business.email
        },
        loadedBusinessDetails: true
      }),
      stopFocusingMessage: () => () => ({
        focusMessage: false
      }),
      hideSubmissionMessage: () => () => ({
        showSubmissionMessage: false
      })
    }),
  lifecycle({
    componentDidMount() {
      const {
        business,
        setBusinessDetails
      } = this.props;
      setBusinessDetails(business);
      this.setState({ componentRef: this });
    },
    componentWillReceiveProps({
      isUpdating,
      loadedBusinessDetails,
      business
    }) {
      const {
        isUpdating: wasUpdating,
        updateFinished,
        setBusinessDetails
      } = this.props;

      if (business && !loadedBusinessDetails) {
        setBusinessDetails(business);
      }

      if (wasUpdating && !isUpdating) {
        updateFinished();
      }
    }
  }),
  withStateHandlers(
    ({ business }) => ({
      businessImage: null,
      businessImageUrl: business.businessImageUrl,
      croppedImageUrl: null,
      logoFileName: business.logoFileName,
      showingCroppingModal: false
    }),
    {
      updateBusinessImage: () => (event, image) => ({
        businessImage: image
      }),
      updateBusinessImageUrl: () => (event, imageUrl) => ({
        businessImageUrl: imageUrl
      }),
      updateLogoFileName: () => (event, logoFileName) => ({
        logoFileName: logoFileName
      }),
      removeCroppedImage: () => () => ({
        croppedImageUrl: null,
        businessImage: null
      }),
      onImageCropped: () => (blob) => ({
        businessImage: blob,
        croppedImageUrl: window.URL.createObjectURL(blob),
        showingCroppingModal: false
      }),
      showCroppingModal: () => () => ({
        showingCroppingModal: true
      }),
      hideCroppingModal: () => () => ({
        showingCroppingModal: false
      })
    }
  ),
  withHandlers({
    updateBusinessDetails: ({
      business,
      businessDetails,
      setValidation,
      startUpdatingBusinessDetails,
      updateBusiness,
      componentRef,
      businessImage,
      logoFileName
    }) => (event) => {
      event.preventDefault();
      event.stopPropagation();

      if (validateBusinessDetailForm({ businessDetails, setValidation })) {
        startUpdatingBusinessDetails('Your business details were successfully updated.');
        updateBusiness({
          businessDetails: {
            ...business,
            ...businessDetails,
            logoFileName
          },
          businessImage
        });
      }
      else if (componentRef && componentRef.formBusinessDetail) {
        handleInvalidForm(componentRef.formBusinessDetail);
      }
    },
    onImageDrop: ({ updateBusinessImage, showCroppingModal, businessImage }) => (acceptedFiles, rejectedFiles) => {
      const acceptedFile = fp.first(acceptedFiles);
      if (acceptedFile) {
        updateBusinessImage(null, acceptedFile);
        showCroppingModal();
      }
    },
    removeImage: ({ updateBusinessImageUrl, updateBusinessImage, updateLogoFileName, removeCroppedImage, hideCroppingModal }) => (e) => {
      e.preventDefault();
      e.stopPropagation();
      updateBusinessImageUrl(null, null);
      updateBusinessImage(null, null);
      updateLogoFileName(null, null);
      removeCroppedImage();
      hideCroppingModal();
    }
  })
)(BusinessDetails);


// WEBPACK FOOTER //
// ./src/containers/pages/app/account-settings/partials/business-details.js